import { forwardRef, ReactNode, useId } from "react";
import ReactSelect, { ClassNamesConfig, Props, components } from 'react-select';
import { twMerge } from "tailwind-merge";
import { badgeControlStyles, controlStyles, disabledControlStyles, styles } from "./styles";
import { replacePeriodToUnderscore } from "@/common/utils/transformers";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { BadgeHelpIcon } from "lucide-react";
import { cn } from "@/common/utils/cn";
import { PlacesType, Tooltip } from "react-tooltip";

export interface SelectProps extends Props {
  label?: string,
  markAsRequired?: boolean;
  labelIcon?: JSX.Element;
  disabled?: boolean,
  options?: { label: string | JSX.Element, value: number | string | null, isDisabled?: boolean, icon?: ReactNode }[];
  optionClassName?: string;
  badge?: boolean;
  dataTestid?: string,
  customStyles?: ClassNamesConfig<unknown>;
  operationKlassToCheckPermission?: string;
  propertiesToCheckPermission?: string[];
  noPermissionText?: string;
  tooltipPlace?: PlacesType;
  tooltipMessage?: string;
  optionLabelFormat?: 'default' | 'icon',
}

const Select = forwardRef<HTMLElement, SelectProps>(({
  label,
  markAsRequired,
  labelIcon,
  className,
  disabled = false,
  options = [],
  optionClassName,
  placeholder = 'Selecione',
  badge = false,
  name,
  noOptionsMessage,
  dataTestid,
  customStyles,
  operationKlassToCheckPermission,
  propertiesToCheckPermission,
  tooltipPlace = "bottom",
  tooltipMessage,
  noPermissionText = 'Você não tem permissão',
  optionLabelFormat = 'default',
  ...props
}) => {
  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();
  const { isDisabled: reactSelectIsDisabled, ...restProps } = props;
  const needsPermission = Boolean(operationKlassToCheckPermission);
  const userHasPermission =
    !needsPermission ||
    (operationKlassToCheckPermission
      ? hasUserPermission(operationKlassToCheckPermission, propertiesToCheckPermission)
      : true);
  const shouldDisable = disabled || !!reactSelectIsDisabled || (needsPermission && !userHasPermission);
  const showPermissionHelper = needsPermission && !userHasPermission && !isUserPermissionsPending;
  const tooltipId = useId();

  const isPermissionNeeded = !!operationKlassToCheckPermission;
  const hasPermission = isPermissionNeeded && hasUserPermission(operationKlassToCheckPermission);

  const Option = (props: any) => {
    if (props.data.tooltip) {
      return (
        <components.Option {...props}>
          <div title={props.data.tooltip} className="relative">
            {props.children}

            <BadgeHelpIcon color="white" strokeWidth={1} size={18} className="right-0 -translate-y-1/2 top-1/2 absolute " />
          </div>
        </components.Option>
      );
    }

    return (
      <components.Option {...props} className={cn(props.className, optionClassName)}>
        {props.children}
      </components.Option>
    );
  };

  const formatOptionLabelWithIcon = (data: any) => (
    <div className='flex items-center gap-2'>
      {data.icon}
      <div>{data.label}</div>
    </div>
  );

  const MenuList = (props: any) => {
    return (
      <div
        onWheel={(e) => e.stopPropagation()}
      >
        <components.MenuList {...props}>
          {props.children}
        </components.MenuList>
      </div>
    );
  };

  const ClearIndicator = (props: any) => (
    <components.ClearIndicator {...props} className="p-0">
      <div
        data-testid={dataTestid || (name ? replacePeriodToUnderscore(name) + '_selector_clear' : '')}
        className="flex items-center justify-center w-9 h-9 hover:opacity-50 duration-150"
      >
        <img src="/clear-select.svg" />
      </div>
    </components.ClearIndicator>
  );

  return (
    <div className={twMerge(className)}>
      <label
        className={twMerge("relative flex flex-col items-start justify-center",)}
        id={dataTestid || (name ? replacePeriodToUnderscore(name) + '_selector' : '')}
        data-testid={dataTestid || (name ? replacePeriodToUnderscore(name) + '_selector' : '')}
        data-tooltip-id={((isPermissionNeeded && !hasPermission) || tooltipMessage) ? tooltipId : ''}
      >
        {label && (
          <div className="flex items-center gap-x-2 mb-1" data-test-role="label">
            <p className={twMerge('text-xs', shouldDisable ? 'text-neutral-4' : '')}>
              {label}
              {markAsRequired && (
                <span className="text-danger-soft"> *</span>
              )}
            </p>

            {labelIcon}
          </div>
        )}

        <ReactSelect
          components={{
            Option,
            MenuList,
            ClearIndicator,
          }}
          options={options}
          styles={styles}
          classNames={customStyles || (badge ? badgeControlStyles : (shouldDisable ? disabledControlStyles : controlStyles))}
          noOptionsMessage={!!noOptionsMessage ? noOptionsMessage : () => 'Nenhuma opção'}
          placeholder={placeholder}
          name={name}
          menuPlacement="auto"
          menuPosition="absolute"
          menuPortalTarget={document.body}
          data-testid={dataTestid || (name ? replacePeriodToUnderscore(name) + '_selector_input' : '')}
          {...restProps}
          formatOptionLabel={optionLabelFormat === "icon" ? formatOptionLabelWithIcon : undefined}
          isDisabled={shouldDisable}
          isLoading={props.isLoading || (isPermissionNeeded && isUserPermissionsPending)}
        />

        {((isPermissionNeeded && !hasPermission && !isUserPermissionsPending) || tooltipMessage) && (
          <Tooltip id={tooltipId} place={tooltipPlace} className="z-50">
            {tooltipMessage || noPermissionText}
          </Tooltip>
        )}
      </label>
      {showPermissionHelper && (
        <span className="text-[11px] text-white-300 mt-1 ml-2 block">
          {noPermissionText || 'Voce nao tem permissão para alterar'}
        </span>
      )}
    </div>
  )
});

export default Select;
